import styled, { css, keyframes } from "styled-components";
import {
    type DisplayProps,
    type WidthProps,
    type FlexboxProps,
    type BorderProps,
    display,
    width,
    flexbox,
    compose,
    border,
} from "styled-system";
import { getBreakpoint } from "../../../../../common-deprecated/themes/common";
import * as Button from "../../../../../common-deprecated/styles/v2/lexus/components/Button";
import { theme } from "../../../../../common-deprecated/styles/v2/lexus/theme/lexusTheme";
import * as ContainerStyles from "../../../../../common-deprecated/styles/lexus/Container";

const overFlowAuto = keyframes`
  0%, 99% {
      overflow: initial;
  }
  100% {
      overflow: auto;
  }
`;

type WrapperType = {
    expanded?: boolean;
    isPdf?: boolean;
};

export const Container = styled(ContainerStyles.default)<{ isStandalone?: boolean }>`
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 0;
    @media (min-width: 1200px) {
        max-width: ${({ isStandalone }) => (isStandalone ? 980 : 780)}px;
    }
`;

export const Wrapper = styled.section<WrapperType>`
    display: flex;
    position: fixed;
    top: 100%;
    left: 0;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    height: 100%;
    background: ${theme.colors.white};
    box-shadow: 0 -4px 11px 0 rgba(0, 0, 0, 0.3);
    transform: translateY(${({ expanded }) => (expanded ? "-100%" : 0)});
    transition: transform 0.5s;
    overflow: initial;
    will-change: transform;
    z-index: ${theme.zIndex.popover}; /* Should be above 1050 to ignore the fixed right sidebar from amaze */
    -webkit-overflow-scrolling: touch;

    ${({ expanded }) =>
        expanded &&
        css`
            animation: ${overFlowAuto} 0.5s linear;
            overflow-y: auto;
            overflow-x: hidden;
        `};

    ${({ isPdf }) =>
        isPdf &&
        css`
            display: block;
            position: static;
            top: 0;
            left: auto;
            width: 100%;
            margin: 0;
            margin-right: 150px;
            padding-bottom: 50px;
            box-shadow: none;
            transform: none;
            overflow: visible;

            ${Body} {
                padding: 0;
            }

            ${Container} {
                padding: 0;
            }
        `};

    html[dir="rtl"] & {
        left: auto;
        right: 0;
        ${({ isPdf }) =>
            isPdf &&
            css`
                left: auto;
                right: auto;
                margin-left: 150px;
                margin-right: 0;
            `}
    }
`;

export const PdfSection = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    margin: ${theme.space[5]}px 0;

    ${Button.ButtonWithIcon} {
        justify-content: center;
    }

    /* Override CSS from foundation-team */
    html[dir="rtl"] & {
        button {
            margin-right: unset;
        }
    }
`;

export const Toggle = styled.div`
    display: flex;
    font-size: 1.3rem;
    cursor: pointer;
`;

export const Row = styled.div`
    padding: 0;
    @media ${getBreakpoint("only", "md")} {
        padding: 0 ${theme.space[1]}px;
    }
`;

const styles = compose(display, width, flexbox, border);
export const Col = styled.div<DisplayProps & WidthProps & FlexboxProps & BorderProps>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${theme.space[3]}px;
    ${styles};
    @media ${getBreakpoint("up", "md")} {
        max-width: 245px;
    }
`;

export const Body = styled.div<{ isPdf?: boolean }>`
    width: 100%;
    padding: ${theme.space[3]}px;
    background-color: ${theme.colors.white};
    -webkit-overflow-scrolling: touch;
    overflow-y: auto;

    @media ${getBreakpoint("only", "md")} {
        margin-left: auto;
        margin-right: auto;
    }

    @media all and (-ms-high-contrast: active) and (-ms-high-contrast: none) {
        /**
            Fix specific for IE11, to add some spacing above the body, else the car images are hidden after the compare header :(
        */
        position: relative;
        top: 100px;
    }

    ${({ isPdf }) =>
        isPdf &&
        css`
            position: static !important;
            top: 0 !important;
            margin-top: 60px !important;
            overflow: visible !important;
        `};
`;

export const CompareItems = styled.div`
    display: flex;
    width: 100%;
    max-width: 100%;
`;

export const Actions = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${theme.space[4]}px 0;

    ${Button.ButtonPrimary},
    ${/* sc-selector */ Button.ButtonSecondary},
    ${Button.ButtonWithIcon} {
        width: 100%;
        &:not(:last-child) {
            margin-bottom: ${theme.space[2]}px;
        }
    }
    button span,
    a span {
        white-space: normal;
        overflow: hidden;
        /* stylelint-disable */
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        display: -webkit-box;
        /* stylelint-enable */
    }
`;
